import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  api,
  onAuthEvent,
  refreshSession,
  setAccessToken,
  type Authenticated,
  type User,
} from '@/api/client';
import type { CuentaRestringida } from '@/api/errors';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: Status;
  user: User | null;
  /** Motivo del último cierre forzado (p. ej. SESSION_REVOKED) para avisar en el login. */
  endedReason: string | null;
  signIn: (result: Authenticated) => void;
  signOut: () => Promise<void>;
  clearEndedReason: () => void;
  /** Suspensión o bloqueo (403) recibido al iniciar o renovar la sesión; el login muestra el modal. */
  restriccion: CuentaRestringida | null;
  mostrarRestriccion: (r: CuentaRestringida) => void;
  cerrarRestriccion: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const canal = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('cs-auth') : null;

/**
 * Sesión del alumno (plan §6 y §13.3). Al montar intenta un refresh con la cookie: si existe,
 * el alumno entra sin volver a escribir su contraseña. Las pestañas del mismo navegador se
 * avisan con BroadcastChannel para cerrar sesión juntas.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const [restriccion, setRestriccion] = useState<CuentaRestringida | null>(null);

  const reset = useCallback(
    (reason: string | null) => {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
      setEndedReason(reason);
      queryClient.clear();
    },
    [queryClient],
  );

  useEffect(() => {
    let vivo = true;
    refreshSession()
      .then((r) => {
        if (!vivo) return;
        if (r) {
          setUser(r.user);
          setStatus('authenticated');
        } else setStatus('anonymous');
      })
      .catch(() => vivo && setStatus('anonymous'));
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(
    () =>
      onAuthEvent((e) => {
        if (e.type !== 'ended') return;
        reset(e.code);
        if (e.restriccion) setRestriccion(e.restriccion);
      }),
    [reset],
  );

  useEffect(() => {
    if (!canal) return;
    const onMessage = (m: MessageEvent<{ type: string }>) => {
      if (m.data.type === 'logout') reset(null);
      if (m.data.type === 'login') {
        void refreshSession().then((r) => {
          if (r) {
            setUser(r.user);
            setStatus('authenticated');
          }
        });
      }
    };
    canal.addEventListener('message', onMessage);
    return () => canal.removeEventListener('message', onMessage);
  }, [reset]);

  const signIn = useCallback((result: Authenticated) => {
    setAccessToken(result.accessToken);
    setUser(result.user);
    setEndedReason(null);
    setRestriccion(null);
    setStatus('authenticated');
    canal?.postMessage({ type: 'login' });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.POST('/api/v1/auth/logout', { body: {} });
    } finally {
      canal?.postMessage({ type: 'logout' });
      reset(null);
    }
  }, [reset]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      endedReason,
      signIn,
      signOut,
      clearEndedReason: () => setEndedReason(null),
      restriccion,
      mostrarRestriccion: setRestriccion,
      cerrarRestriccion: () => setRestriccion(null),
    }),
    [status, user, endedReason, signIn, signOut, restriccion],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de <AuthProvider>');
  return ctx;
}
